// ============================================================================
// FileUploadField — reusable drag-drop + camera-capture file uploader
// ============================================================================
// Wraps fileStorageService.upload and exposes the StoredFileInfo via onChange.
// Suitable for guest ID document capture (QSM-CHKIN-001) and any other place
// that needs a simple, validated, server-uploaded file.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { fileStorageService } from 'api/services/fileStorageService';
import type { StoredFileInfo } from 'api/types/fileStorage.types';
import notificationService from 'services/notificationService';
import FilePreviewModal from '../FilePreviewModal/FilePreviewModal';

const DEFAULT_ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,application/pdf';
const DEFAULT_MAX_BYTES = 10 * 1024 * 1024; // 10 MB — matches server default
const DEFAULT_ALLOWED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'application/pdf',
];

export interface FileUploadFieldProps {
  /** Logical bucket — sent to the server as `?category=`. */
  category: string;
  /** Current stored value (from a previous upload). When set, the preview renders it. */
  value?: StoredFileInfo | null;
  /** Fires on successful upload OR on clear (null). */
  onChange: (info: StoredFileInfo | null) => void;
  /** Optional label rendered above the drop zone. */
  label?: React.ReactNode;
  /** Disable interactions (e.g. read-only/saving state). */
  disabled?: boolean;
  /** Required-marker affects only the label rendering. */
  required?: boolean;
  /** Inline error rendered below the drop zone. */
  error?: string;
  /** Allowed MIME types. Defaults to the standard image+PDF set. */
  allowedTypes?: string[];
  /** Max size in bytes. Defaults to 10 MB. */
  maxBytes?: number;
  /** Show camera-capture mode (image only). Default true. */
  enableCamera?: boolean;
  /** Custom helper text under the drop zone. */
  helperText?: string;
  /**
   * Compact inline mode — hides the big drag/drop zone and renders only a
   * small Upload + Camera button pair. Used inside repeating list rows
   * (e.g. multi-doc ID capture) where each row would otherwise eat a lot
   * of vertical space.
   */
  compact?: boolean;
}

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isImage = (contentType?: string) => !!contentType?.startsWith('image/');

const CameraIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

const FileUploadField: React.FC<FileUploadFieldProps> = ({
  category,
  value,
  onChange,
  label,
  disabled = false,
  required = false,
  error,
  allowedTypes = DEFAULT_ALLOWED_TYPES,
  maxBytes = DEFAULT_MAX_BYTES,
  enableCamera = true,
  helperText,
  compact = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  // Lightbox state for the in-app file preview (replaces the old new-tab behaviour).
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Fetch the file via authenticated axios → blob → object URL.
  // Raw <img src=https://api/.../download?path=...> doesn't carry the
  // Authorization or X-Tenant-ID headers, so the server rejects it
  // with 401 / MISSING_TENANT_ID. The blob-URL approach side-steps that
  // entirely — both <img> and <iframe> see a local blob: URL that needs
  // no auth. We fetch for EVERY allowed type (image + PDF) so the View
  // button is available regardless of MIME — the lightbox picks the
  // right renderer (img / iframe).
  useEffect(() => {
    let cancelled = false;
    let urlToRevoke: string | null = null;

    if (value?.relativePath) {
      fileStorageService
        .openAsObjectUrl(value.relativePath)
        .then((blobUrl) => {
          if (cancelled) {
            URL.revokeObjectURL(blobUrl);
            return;
          }
          urlToRevoke = blobUrl;
          setPreviewUrl(blobUrl);
        })
        .catch(() => {
          // Silent — preview is non-critical. The file row still shows
          // name / size / type and the Remove button.
          if (!cancelled) setPreviewUrl(null);
        });
    } else {
      setPreviewUrl(null);
    }

    return () => {
      cancelled = true;
      if (urlToRevoke) URL.revokeObjectURL(urlToRevoke);
    };
  }, [value]);

  const validate = useCallback(
    (file: File): string | null => {
      if (!allowedTypes.includes(file.type)) {
        return `Unsupported file type "${file.type || 'unknown'}". Allowed: ${allowedTypes.join(', ')}.`;
      }
      if (file.size > maxBytes) {
        return `File is ${formatBytes(file.size)} — exceeds the ${formatBytes(maxBytes)} limit.`;
      }
      return null;
    },
    [allowedTypes, maxBytes],
  );

  const handleFile = useCallback(
    async (file: File | undefined | null) => {
      if (!file) return;
      const validationError = validate(file);
      if (validationError) {
        notificationService.toast.error(validationError);
        return;
      }
      setIsUploading(true);
      try {
        const info = await fileStorageService.upload(file, category);
        onChange(info);
        notificationService.toast.success('File uploaded.');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'File upload failed.';
        notificationService.toast.error(msg);
      } finally {
        setIsUploading(false);
      }
    },
    [validate, category, onChange],
  );

  const handleSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      handleFile(file);
      // Reset so the same file can be re-selected after an error.
      e.target.value = '';
    },
    [handleFile],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragOver(false);
      if (disabled || isUploading) return;
      const file = e.dataTransfer.files?.[0];
      handleFile(file);
    },
    [disabled, isUploading, handleFile],
  );

  const handleClear = useCallback(() => {
    if (disabled || isUploading) return;
    onChange(null);
  }, [disabled, isUploading, onChange]);

  const openFilePicker = () => fileInputRef.current?.click();
  const openCameraPicker = () => cameraInputRef.current?.click();

  // ── Render preview ───────────────────────────────────────────────────────
  const renderPreview = () => {
    if (!value) return null;

    // ── Compact preview ────────────────────────────────────────────────
    // No surrounding card, no thumbnail block, no separate size+type line.
    // Just: filename + tiny File/Image icon + View + Remove. Designed to
    // fit inside a narrow inline grid column (multi-doc rows).
    if (compact) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.375rem',
            minWidth: 0,
          }}
          title={`${value.fileName} · ${formatBytes(value.sizeBytes)} · ${value.contentType}`}
        >
          <IconSystem.file size={14} />
          <span
            style={{
              fontSize: '0.75rem',
              color: 'var(--text-primary)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flex: 1,
              minWidth: 0,
            }}
          >
            {value.fileName}
          </span>
          {previewUrl && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setPreviewModalOpen(true)}
              style={{ padding: '0.2rem 0.45rem', fontSize: '0.7rem', display: 'inline-flex', alignItems: 'center', gap: '0.2rem', flexShrink: 0 }}
              title="Preview file"
            >
              <IconSystem.eye size={12} /> View
            </button>
          )}
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleClear}
            disabled={disabled || isUploading}
            style={{ padding: '0.2rem 0.45rem', fontSize: '0.7rem', display: 'inline-flex', alignItems: 'center', gap: '0.2rem', flexShrink: 0 }}
            title="Remove this file"
          >
            <IconSystem.trash size={12} /> Remove
          </button>
        </div>
      );
    }

    // ── Full preview (standalone / single-doc) ─────────────────────────
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          padding: '0.625rem 0.75rem',
          border: `1px solid var(--form-border-color, #334155)`,
          borderRadius: '6px',
          background: 'var(--bg-secondary)',
        }}
      >
        {previewUrl && isImage(value.contentType) ? (
          <img
            src={previewUrl}
            alt={value.fileName}
            style={{
              width: '48px',
              height: '48px',
              objectFit: 'cover',
              borderRadius: '4px',
              border: '1px solid var(--border-color)',
            }}
          />
        ) : (
          <div
            style={{
              width: '48px',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--bg-primary)',
              borderRadius: '4px',
              border: '1px solid var(--border-color)',
              color: 'var(--text-secondary)',
            }}
            title={value.contentType}
          >
            <IconSystem.file size={20} />
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: '0.8125rem',
              fontWeight: 500,
              color: 'var(--text-primary)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={value.fileName}
          >
            {value.fileName}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
            {formatBytes(value.sizeBytes)} · {value.contentType}
          </div>
        </div>
        {previewUrl && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setPreviewModalOpen(true)}
            style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}
            title="Preview file"
          >
            <IconSystem.eye size={14} /> View
          </button>
        )}
        <button
          type="button"
          className="btn btn-danger"
          onClick={handleClear}
          disabled={disabled || isUploading}
          style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}
          title="Remove this file and clear the upload"
        >
          <IconSystem.trash size={14} /> Remove
        </button>
      </div>
    );
  };

  const dropZoneBorder = error
    ? '#ef4444'
    : isDragOver
      ? '#2563eb'
      : 'var(--form-border-color, #334155)';

  return (
    <div
      // In compact mode skip the .form-group bottom margin and tighten the
      // inner gap — the field is meant to sit flush inside a row layout
      // rather than be a stand-alone form-field with breathing room below.
      className={compact ? undefined : 'form-group'}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: compact ? '0.125rem' : '0.375rem',
        marginBottom: 0,
      }}
    >
      {label && (
        <label
          className="form-label"
          style={{ fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.375rem' }}
        >
          {label} {required && <span className="text-danger">*</span>}
        </label>
      )}

      {value ? (
        renderPreview()
      ) : compact ? (
        // Compact mode — no drop-zone, just inline Upload + Camera buttons.
        // Used by multi-row consumers (e.g. multi-doc ID capture) where
        // repeating a big drop-zone per row would dominate the form.
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: error ? '0.25rem 0' : 0,
            opacity: disabled ? 0.6 : 1,
          }}
        >
          {isUploading ? (
            <>
              <div
                style={{
                  width: '1rem',
                  height: '1rem',
                  border: '2px solid var(--border-color)',
                  borderTopColor: '#2563eb',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }}
              />
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Uploading…</span>
            </>
          ) : (
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={openFilePicker}
                disabled={disabled}
                style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}
                title={disabled ? 'Pick an ID type and enter a number first' : 'Choose a file to upload'}
              >
                <IconSystem.upload size={12} /> Upload
              </button>
              {enableCamera && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={openCameraPicker}
                  disabled={disabled}
                  style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}
                  title={disabled ? 'Pick an ID type and enter a number first' : 'Capture a photo'}
                >
                  <CameraIcon size={12} /> Camera
                </button>
              )}
            </>
          )}
        </div>
      ) : (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled && !isUploading) setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={handleDrop}
          style={{
            border: `1.5px dashed ${dropZoneBorder}`,
            borderRadius: '6px',
            padding: '1rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            background: isDragOver ? 'rgba(37, 99, 235, 0.05)' : 'var(--bg-secondary)',
            cursor: disabled || isUploading ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
            transition: 'border-color 120ms, background 120ms',
            minHeight: '90px',
          }}
        >
          {isUploading ? (
            <>
              <div
                style={{
                  width: '1.5rem',
                  height: '1.5rem',
                  border: '3px solid var(--border-color)',
                  borderTopColor: '#2563eb',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }}
              />
              <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>Uploading...</span>
            </>
          ) : (
            <>
              <IconSystem.upload size={22} />
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', textAlign: 'center' }}>
                Drag & drop, or use the buttons below
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={openFilePicker}
                  disabled={disabled}
                  style={{ padding: '0.375rem 0.75rem', fontSize: '0.8125rem' }}
                >
                  <IconSystem.upload size={14} /> &nbsp;Upload File
                </button>
                {enableCamera && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={openCameraPicker}
                    disabled={disabled}
                    style={{ padding: '0.375rem 0.75rem', fontSize: '0.8125rem' }}
                  >
                    <CameraIcon size={14} /> &nbsp;Capture Photo
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Hidden inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept={allowedTypes.join(',') || DEFAULT_ACCEPT}
        onChange={handleSelect}
        style={{ display: 'none' }}
      />
      {enableCamera && (
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleSelect}
          style={{ display: 'none' }}
        />
      )}

      {helperText && !error && (
        <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{helperText}</div>
      )}
      {error && <div className="form-error-message">{error}</div>}

      {/* In-app preview lightbox — reuses the shared FilePreviewModal so
          BookingViewModal and any future consumer get the same UX without
          duplicating the lightbox markup. The shared component refetches
          the blob through authenticated axios; we already have one in
          previewUrl from the row thumbnail, but a second small fetch on
          modal open is acceptable for the simplicity it buys. */}
      {previewModalOpen && value && (
        <FilePreviewModal
          relativePath={value.relativePath}
          fileName={value.fileName}
          contentType={value.contentType}
          sizeLabel={formatBytes(value.sizeBytes)}
          onClose={() => setPreviewModalOpen(false)}
        />
      )}
    </div>
  );
};

export default FileUploadField;
